
import { useState, useEffect, useCallback } from "react";
import { db } from "../../firebaseClient";
import { computeDashboardSummaryFromTodos, TodoItem, ComputedSummary } from "../services/todosSummary";

// --- Singleton State ---
let globalResult: ComputedSummary | null = null;
let globalRawTodos: TodoItem[] = [];

let listeners: Array<() => void> = [];
let unsubscribe: (() => void) | null = null;
let reconnectTimer: any = null;

let connectionStatus: "idle" | "connecting" | "connected" | "error" = "idle";
let connectionError: string | null = null;
let lastSnapshotAt: Date | null = null;

function notifyListeners() {
    listeners.forEach(l => l());
}

function stopListener() {
    if (unsubscribe) {
        console.log("[useTodosLive] Stopping listener...");
        unsubscribe();
        unsubscribe = null;
    }
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    connectionStatus = "idle";
    notifyListeners();
}

function startListener() {
    if (unsubscribe) return; // Already active

    console.log("[useTodosLive] Starting listener on /todos (OPEN)...");
    connectionStatus = "connecting";
    connectionError = null;
    notifyListeners();

    try {
        // CHANGED: Listen to 'todos' index directly.
        const q = db.collection("todos")
            .where("todoStatus", "==", "OPEN")
            .limit(2000); 

        unsubscribe = q.onSnapshot(
            (snap) => {
                const todos = snap.docs.map(d => ({ id: d.id, ...d.data() })) as TodoItem[];
                
                globalRawTodos = todos;
                globalResult = computeDashboardSummaryFromTodos(todos);
                
                lastSnapshotAt = new Date();
                connectionStatus = "connected";
                connectionError = null;
                
                notifyListeners();
            },
            (err) => {
                console.error("[useTodosLive] Snapshot Error:", err);
                connectionStatus = "error";
                connectionError = err.message;
                notifyListeners();
                
                stopListener();
                console.log("[useTodosLive] Scheduling reconnect in 5s...");
                reconnectTimer = setTimeout(startListener, 5000);
            }
        );
    } catch (e: any) {
        console.error("[useTodosLive] Setup Error:", e);
        connectionStatus = "error";
        connectionError = e.message;
        notifyListeners();
    }
}

export function useTodosLive() {
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        const myListener = () => forceUpdate(n => n + 1);
        listeners.push(myListener);
        
        if (connectionStatus === "idle") {
            startListener();
        }

        return () => {
            listeners = listeners.filter(l => l !== myListener);
        };
    }, []);

    const reconnect = useCallback(() => {
        stopListener();
        setTimeout(startListener, 500);
    }, []);

    return {
        liveStatus: connectionStatus,
        liveMsg: connectionError || (connectionStatus === 'connected' ? "Live (Source: Todos Index)" : "Connecting..."),
        summary: globalResult, 
        rawTodos: globalRawTodos,
        rawDocs: [], 
        meta: globalResult?.meta, 
        lastSnapshotAt,
        reconnect,
        startListener,
        stopListener
    };
}
